import React from 'react';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { CardPostsLocale } from '@/features/publication-wall/constants/labels';
import { PostCard } from '../PostCard/PostCard';
import { ChannelIconData } from '@/core/services/ChannelsService';
import { useTranslation } from 'react-i18next';
import { BdsIllustration, BdsButton } from '@/core/constants/webComponents';
import './PostCardList.scss';

export type PostCardListStateType = 'noResults' | 'loadingTime' | 'offline' | 'error';

const ILLUSTRATION_MAP: Record<PostCardListStateType, string> = {
  noResults: 'search-1',
  loadingTime: 'time-1',
  offline: 'robot-6',
  error: 'atention',
};

interface PostCardListProps {
  posts: Post[];
  locale: CardPostsLocale;
  channels?: Record<string, ChannelIconData>;
  existingTags?: string[];
  stateType?: PostCardListStateType;
}

const ACTIONS_ENABLED = new Set<PostCardListStateType>(['loadingTime', 'offline', 'error']);

export const PostCardList: React.FC<PostCardListProps> = ({ 
  posts, 
  locale, 
  channels = {}, 
  existingTags = [],
  stateType = 'noResults'
}) => {
  const { t } = useTranslation();

  const handleReload = () => {
    globalThis.location.reload();
  };

  const showActions = ACTIONS_ENABLED.has(stateType);

  if (posts.length === 0 || stateType === 'error') {
    const illustrationName = ILLUSTRATION_MAP[stateType];

    const titleKey = `cardPosts.emptyState.${stateType}.title`;
    const descriptionKey = `cardPosts.emptyState.${stateType}.description`;
    const buttonKey = `cardPosts.emptyState.${stateType}.button`;

    const isOffline = stateType === 'offline' && !navigator.onLine;

    return (
      <output 
        className={`post-card-list post-card-list--empty ${isOffline ? 'post-card-list--offline' : ''}`} 
        aria-live="polite"
      >
        {!isOffline && (
          <span className="post-card-list__empty-icon" key={`${stateType}-illustration`}>
            <BdsIllustration 
              type="default"
              name={illustrationName}
              alt={t(titleKey)}
              class="post-card-list__empty-illustration"
            />
          </span>
        )}
      <div className="post-card-list__empty-message" key={`${stateType}-content`}>
        <h2 className="post-card-list__empty-title">{t(titleKey)}</h2>
        <p className="post-card-list__empty-description">{t(descriptionKey)}</p>
        {showActions && (
          <div className="post-card-list__empty-actions">
            <BdsButton onClick={handleReload} variant="primary">
              {t(buttonKey)}
            </BdsButton>
          </div>
        )}
      </div>
      </output>
    );
  }

  return (
    <div className="post-card-list">
      {posts.map((post) => (
        <PostCard
          key={post._id}
          post={post}
          locale={locale}
          channels={channels}
          existingTags={existingTags}
        />
      ))}
    </div>
  );
};
